/**
 * Pure page assembler (Wave 3, Task 3.1).
 *
 * Turns a set of design frames + the CanvasDoc into a standalone static site:
 * resolved HTML, compiled theme + Tailwind CSS, and the referenced asset blobs.
 * No React, no store, no network — all heavy inputs (Tailwind compile, asset
 * bytes) are injected so the assembler is unit-testable without infra. The
 * browser/proxy publish flow wires the real deps; tests pass stubs.
 *
 * v1 scope: HTML design frames only (free/main/instance). TSX frames and tool
 * frames are skipped. Motion + scripts are layered on in Tasks 3.2/3.1-Step3.
 */
import type { CanvasDoc, Frame } from '../store/types';
import { framesById, getRenderHtml } from '../store/commands';
import { compileThemes } from '../style/theme-compiler';
import { browserCompileFn } from '../style/tailwind-compiler';
import { transpile } from '../script/compileScript';
import { MOTION_RUNTIME_SOURCE } from './motionRuntime.bundle';
import type { MotionTimeline } from '../store/types';

/** Injected seams — default to the real browser implementations. */
export interface AssembleDeps {
  /** Tailwind compile: (cssWithTheme, candidateClasses) => utility CSS. */
  compileCss?: (css: string, candidates: string[]) => Promise<string>;
  /** Asset lookup by `asset://` hash → raw bytes + file extension. */
  getAsset?: (hash: string) => Promise<{ bytes: ArrayBuffer; ext: string } | null>;
  /** Full, self-booting motion runtime JS (the bundled IIFE) written as
   *  `motion.js` when any frame animates. Defaults to the built bundle. */
  motionRuntimeSource?: string;
}

export interface AssembledPage {
  /** Text files keyed by relative path (e.g. 'index.html', 'styles.css'). */
  files: Map<string, string>;
  /** Binary files keyed by relative path (e.g. 'assets/<hash>.<ext>'). */
  assets: Map<string, Blob>;
}

/** Attributes that are editor-only — stripped from published markup. data-cf-id
 *  is deliberately KEPT (motion targets + future live-edit both need it; inert
 *  in production). */
const EDITOR_ATTRS = ['contenteditable', 'data-slot', 'data-frame-id', 'draggable', 'spellcheck'];

const ASSET_REF = /asset:\/\/([A-Za-z0-9._-]+)/g;

function isHtmlDesignFrame(f: Frame | undefined): f is Frame {
  return !!f && (f.kind === 'free' || f.kind === 'main' || f.kind === 'instance');
}

/** Parse → strip editor attrs (keep data-cf-id) → re-serialize a frame's body. */
function cleanMarkup(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const el of Array.from(doc.body.querySelectorAll('*'))) {
    for (const attr of EDITOR_ATTRS) el.removeAttribute(attr);
  }
  return doc.body.innerHTML;
}

/** Collect Tailwind candidate classes from every `class="..."` in the markup. */
function collectClasses(html: string, into: Set<string>): void {
  const re = /class\s*=\s*"([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    for (const c of m[1].split(/\s+/)) if (c) into.add(c);
  }
}

export async function assemblePage(
  frameIds: string[],
  doc: CanvasDoc,
  deps: AssembleDeps = {},
): Promise<AssembledPage> {
  const compileCss = deps.compileCss ?? browserCompileFn;
  const getAsset = deps.getAsset ?? null;

  const byId = framesById(doc.frames);
  const frames = frameIds.map((id) => byId.get(id)).filter(isHtmlDesignFrame);

  // Bounding box of the selection → frames laid out absolutely, origin-normalized.
  const minX = Math.min(...frames.map((f) => f.x));
  const minY = Math.min(...frames.map((f) => f.y));
  const maxX = Math.max(...frames.map((f) => f.x + f.w));
  const maxY = Math.max(...frames.map((f) => f.y + (f.h ?? 0)));

  const candidates = new Set<string>();
  const wrappers: string[] = [];
  const scripted: string[] = []; // frame ids that emitted a script module
  const motionScripts: string[] = []; // embedded timeline <script> tags
  const files = new Map<string, string>();
  for (const f of frames) {
    const resolved = getRenderHtml(f, byId);
    const cleaned = cleanMarkup(resolved);
    collectClasses(cleaned, candidates);
    const themeId = f.themeId ?? doc.activeThemeId;
    const themeAttr = themeId ? ` data-theme="${themeId}"` : '';
    const left = f.x - minX;
    const top = f.y - minY;
    const h = f.h ?? 0;
    wrappers.push(
      `<div class="cf-frame" data-cf-frame="${f.id}"${themeAttr} ` +
        `style="position:absolute; left: ${left}px; top: ${top}px; ` +
        `width: ${f.w}px;${h ? ` height: ${h}px;` : ''}">${cleaned}</div>`,
    );
    const src = (f.kind === 'free' || f.kind === 'main') && f.script ? f.script : undefined;
    if (src) {
      files.set(`script-${f.id}.js`, transpile(src));
      scripted.push(f.id);
    }

    // Motion: own timeline (free/main) or inherited from the Main (instance).
    let tl: MotionTimeline | undefined;
    if ((f.kind === 'free' || f.kind === 'main') && f.motion) {
      tl = f.motion;
    } else if (f.kind === 'instance') {
      const main = byId.get(f.mainId);
      if (main?.kind === 'main') tl = main.motion;
    }
    if (tl && tl.tracks.length > 0) {
      motionScripts.push(
        `<script type="application/json" data-cf-motion="${f.id}">${JSON.stringify(tl)}</script>`,
      );
    }
  }

  const bboxW = isFinite(maxX - minX) ? maxX - minX : 0;
  const bboxH = isFinite(maxY - minY) ? maxY - minY : 0;
  const container =
    `<div class="cf-page" style="position:relative; width: ${bboxW}px; height: ${bboxH}px;">` +
    wrappers.join('') +
    `</div>`;

  // CSS: Tailwind utilities (compiled against the theme registration) + the
  // per-theme scope blocks that the data-theme attributes cascade over.
  const { themeBlock, scopeBlocks } = compileThemes(doc.themes ?? []);
  const cssInput = `@import "tailwindcss";\n${themeBlock}`;
  const tw = await compileCss(cssInput, [...candidates]);
  const css = `${tw}\n${scopeBlocks}`;

  // Script loader: import each frame's transpiled setup(root, ctx) module and run
  // it on DOMContentLoaded, scoped to the frame wrapper, with a minimal ctx.
  const loader = scripted.length ? buildLoader(scripted) : '';

  // Motion: embed per-frame timeline JSON + ship the self-booting runtime.
  let motionTag = '';
  if (motionScripts.length) {
    files.set('motion.js', deps.motionRuntimeSource ?? MOTION_RUNTIME_SOURCE);
    motionTag = `\n${motionScripts.join('\n')}\n<script src="motion.js"></script>`;
  }

  // Build index.html, then rewrite asset:// refs → relative paths + emit blobs.
  const assets = new Map<string, Blob>();
  let indexHtml =
    `<!doctype html>\n<html>\n<head>\n<meta charset="utf-8">\n` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">\n` +
    `<link rel="stylesheet" href="styles.css">\n</head>\n<body>\n${container}${motionTag}\n${loader}\n</body>\n</html>\n`;

  if (getAsset) {
    const hashes = new Set<string>();
    let m: RegExpExecArray | null;
    ASSET_REF.lastIndex = 0;
    while ((m = ASSET_REF.exec(indexHtml))) hashes.add(m[1]);

    const pathByHash = new Map<string, string>();
    for (const hash of hashes) {
      const got = await getAsset(hash);
      if (!got) continue; // unresolved ref left as-is (honest broken link)
      const path = `assets/${hash}.${got.ext}`;
      pathByHash.set(hash, path);
      assets.set(path, new Blob([got.bytes]));
    }
    indexHtml = indexHtml.replace(ASSET_REF, (whole, hash) => pathByHash.get(hash) ?? whole);
  }

  files.set('index.html', indexHtml);
  files.set('styles.css', css);

  return { files, assets };
}

/** Inline ES-module loader that boots each frame's published script. */
function buildLoader(ids: string[]): string {
  const list = ids.map((id) => `[${JSON.stringify(id)}, ${JSON.stringify(`./script-${id}.js`)}]`).join(', ');
  return (
    `<script type="module">\n` +
    `const mods = [${list}];\n` +
    `const makeCtx = (root) => ({\n` +
    `  state: {},\n` +
    `  setState(p) { Object.assign(this.state, p); },\n` +
    `  theme: {},\n` +
    `  emit(name, detail) { root.dispatchEvent(new CustomEvent(name, { detail, bubbles: true })); },\n` +
    `  invalidate() {},\n` +
    `});\n` +
    `addEventListener('DOMContentLoaded', async () => {\n` +
    `  for (const [id, path] of mods) {\n` +
    `    const root = document.querySelector('[data-cf-frame="' + id + '"]');\n` +
    `    if (!root) continue;\n` +
    `    try { const m = await import(path); m.default && m.default(root, makeCtx(root)); }\n` +
    `    catch (e) { console.error('cf script failed', id, e); }\n` +
    `  }\n` +
    `});\n` +
    `</script>`
  );
}
